import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, IconButton, Chip, Tabs, Tab, TextField, Switch,
  Select, MenuItem, FormControl, InputLabel, Dialog, DialogContent, DialogActions,
  Divider, Stepper, Step, StepLabel, ToggleButtonGroup, ToggleButton, OutlinedInput,
  Autocomplete, Alert, Table, TableHead, TableBody, TableRow, TableCell,
  LinearProgress, Collapse,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import { Page, Card } from '../ui';

// Значения — под реальный Facebook Marketing API (v23). Слева API-значение, справа подпись.
const OBJECTIVES = [
  { v: 'OUTCOME_LEADS', l: 'Лиды' },
  { v: 'OUTCOME_SALES', l: 'Продажи / конверсии' },
  { v: 'OUTCOME_TRAFFIC', l: 'Трафик' },
  { v: 'OUTCOME_ENGAGEMENT', l: 'Вовлечённость' },
  { v: 'OUTCOME_AWARENESS', l: 'Охват / узнаваемость' },
  { v: 'OUTCOME_APP_PROMOTION', l: 'Установки приложения' },
];
const BUDGET_TYPES = [{ v: 'daily', l: 'Дневной' }, { v: 'lifetime', l: 'На весь срок' }];
const _CONV_OPT = ['OFFSITE_CONVERSIONS', 'CONVERSIONS', 'VALUE'];   // оптимизации, требующие пиксель
const BID_STRATEGIES = [
  { v: 'LOWEST_COST_WITHOUT_CAP', l: 'Авто (минимальная цена)' },
  { v: 'LOWEST_COST_WITH_BID_CAP', l: 'Предельная ставка (bid cap)' },
  { v: 'COST_CAP', l: 'Целевая цена (cost cap)' },
];
const OPT_GOALS = [
  { v: 'LEAD_GENERATION', l: 'Лиды (форма)' },
  { v: 'OFFSITE_CONVERSIONS', l: 'Конверсии на сайте' },
  { v: 'LANDING_PAGE_VIEWS', l: 'Просмотры лендинга' },
  { v: 'LINK_CLICKS', l: 'Клики по ссылке' },
  { v: 'REACH', l: 'Охват' },
  { v: 'IMPRESSIONS', l: 'Показы' },
  { v: 'THRUPLAY', l: 'Просмотры видео (ThruPlay)' },
  { v: 'POST_ENGAGEMENT', l: 'Вовлечённость' },
];
const BILLING = [{ v: 'IMPRESSIONS', l: 'За показы' }, { v: 'LINK_CLICKS', l: 'За клики' }];
const ATTRIBUTION = [
  { v: '7d_click_1d_view', l: '7 дн. клик + 1 дн. просмотр' },
  { v: '7d_click', l: '7 дней клик' },
  { v: '1d_click', l: '1 день клик' },
  { v: '1d_view', l: '1 день просмотр' },
];
const CONV_EVENTS = [
  { v: '', l: 'Не выбрано' },
  { v: 'LEAD', l: 'Лид' },
  { v: 'PURCHASE', l: 'Покупка' },
  { v: 'COMPLETE_REGISTRATION', l: 'Регистрация' },
  { v: 'ADD_TO_CART', l: 'Добавление в корзину' },
  { v: 'INITIATE_CHECKOUT', l: 'Начало оформления' },
  { v: 'SUBSCRIBE', l: 'Подписка' },
  { v: 'CONTACT', l: 'Контакт' },
];
const CTAS = [
  { v: 'LEARN_MORE', l: 'Подробнее' },
  { v: 'SIGN_UP', l: 'Зарегистрироваться' },
  { v: 'SHOP_NOW', l: 'В магазин' },
  { v: 'SUBSCRIBE', l: 'Подписаться' },
  { v: 'DOWNLOAD', l: 'Скачать' },
  { v: 'GET_OFFER', l: 'Получить предложение' },
  { v: 'APPLY_NOW', l: 'Подать заявку' },
  { v: 'CONTACT_US', l: 'Связаться' },
  { v: 'BOOK_TRAVEL', l: 'Забронировать' },
  { v: 'GET_QUOTE', l: 'Получить расчёт' },
  { v: 'SEND_MESSAGE', l: 'Написать' },
];
const GENDERS = [{ v: 'all', l: 'Любой' }, { v: 'male', l: 'Мужчины' }, { v: 'female', l: 'Женщины' }];
const PLACEMENTS = [
  { v: 'facebook:feed', l: 'Лента FB' },
  { v: 'instagram:stream', l: 'Лента IG' },
  { v: 'facebook:facebook_reels', l: 'FB Reels' },
  { v: 'instagram:reels', l: 'IG Reels' },
  { v: 'instagram:story', l: 'IG Истории' },
  { v: 'facebook:story', l: 'FB Истории' },
  { v: 'audience_network:classic', l: 'Audience Network' },
  { v: 'messenger:messenger_home', l: 'Messenger' },
];
// FB: платформа устройства (device_platforms) + ОС только для мобайла (user_os)
const DEVICE_PLATFORMS = [{ v: 'all', l: 'Все устройства' }, { v: 'desktop', l: 'Только десктоп' }, { v: 'mobile', l: 'Только мобайл' }];
const MOBILE_OS = [{ v: 'all', l: 'Любая ОС' }, { v: 'Android', l: 'Android' }, { v: 'iOS', l: 'iOS' }];
const COUNTRIES = [
  { v: 'US', l: 'США' }, { v: 'GB', l: 'Великобритания' }, { v: 'CA', l: 'Канада' },
  { v: 'AU', l: 'Австралия' }, { v: 'DE', l: 'Германия' }, { v: 'FR', l: 'Франция' },
  { v: 'ES', l: 'Испания' }, { v: 'IT', l: 'Италия' }, { v: 'NL', l: 'Нидерланды' },
  { v: 'PL', l: 'Польша' }, { v: 'PT', l: 'Португалия' }, { v: 'SE', l: 'Швеция' },
  { v: 'MX', l: 'Мексика' }, { v: 'BR', l: 'Бразилия' }, { v: 'AR', l: 'Аргентина' },
  { v: 'CO', l: 'Колумбия' }, { v: 'CL', l: 'Чили' }, { v: 'PE', l: 'Перу' },
  { v: 'IN', l: 'Индия' }, { v: 'ID', l: 'Индонезия' }, { v: 'PH', l: 'Филиппины' },
  { v: 'TR', l: 'Турция' }, { v: 'SA', l: 'Саудовская Аравия' }, { v: 'AE', l: 'ОАЭ' },
  { v: 'ZA', l: 'ЮАР' }, { v: 'NG', l: 'Нигерия' }, { v: 'UA', l: 'Украина' },
  { v: 'KZ', l: 'Казахстан' }, { v: 'JP', l: 'Япония' }, { v: 'KR', l: 'Южная Корея' },
  { v: 'TH', l: 'Таиланд' }, { v: 'VN', l: 'Вьетнам' }, { v: 'RO', l: 'Румыния' },
];
const ROUND = { '& .MuiOutlinedInput-root': { borderRadius: '6px' } };

const objLabel = (v) => (OBJECTIVES.find((o) => o.v === v) || {}).l || v;
const labelOf = (list, v) => (list.find((x) => x.v === v) || {}).l || v;

// Заголовок секции в диалоге
function Sec({ children }) {
  return (
    <Typography sx={{
      fontFamily: '"JetBrains Mono", monospace', fontSize: 11, fontWeight: 600,
      letterSpacing: '0.12em', textTransform: 'uppercase', color: 'text.secondary', mt: 2.75, mb: 1.25,
    }}>{children}</Typography>
  );
}

const emptyBundle = () => ({
  name: '', objective: 'OUTCOME_LEADS',
  config: {
    // кампания
    budget_type: 'daily', budget_amount: 20, budget_level: 'campaign',
    bid_strategy: 'LOWEST_COST_WITHOUT_CAP', bid_amount: '',
    optimization_goal: 'LEAD_GENERATION', billing_event: 'IMPRESSIONS',
    attribution: '7d_click_1d_view', conversion_event: '',
    // аудитория
    countries: [], age_min: 18, age_max: 65, gender: 'all', interests: '',
    // плейсменты и устройства
    auto_placements: true, placements: [],
    device_platform: 'all', mobile_os: 'all',
    // объявление (страница/IG/пиксель/ссылка выбираются при запуске, для каждого аккаунта свои)
    link_url: '',
    primary_text: '', headline: '', description: '', cta: 'LEARN_MORE',
    // названия с макросами + доп. таргет
    campaign_name: '{{bundle.name}}',
    adset_name: '{{bundle.name}} - adset',
    ad_name: '{{bundle.name}} - ad {{ad.number}}',
    languages: '', targeting_expansion: true, wifi_only: false,
  },
});

const STATUS_COLOR = { queued: 'info', running: 'warning', done: 'success', error: 'error', partial: 'warning', pending: 'default' };
const STATUS_LABEL = { queued: 'В очереди', running: 'Выполняется', done: 'Готово', error: 'Ошибка', partial: 'Частично', pending: 'Ожидает' };
const ITEM_COLOR = { pending: 'default', running: 'warning', done: 'success', error: 'error' };
const ITEM_LABEL = { pending: 'ждёт', running: 'льётся', done: 'готово', error: 'ошибка' };

/* ---------- Вкладка: Связки ---------- */
function BundlesTab() {
  const [bundles, setBundles] = useState([]);
  const [open, setOpen] = useState(false);
  const [b, setB] = useState(emptyBundle());
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { axios.get('/bundles').then((r) => setBundles(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const setCfg = (k, v) => setB((p) => ({ ...p, config: { ...p.config, [k]: v } }));
  const editBundle = (bn) => {
    const base = emptyBundle();   // добавляем недостающие поля (для старых связок)
    setB({ ...base, ...bn, config: { ...base.config, ...(bn.config || {}) } });
    setOpen(true);
  };
  const newBundle = () => { setB(emptyBundle()); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (b.id) await axios.put(`/bundles/${b.id}`, b);
      else await axios.post('/bundles', b);
      setOpen(false); load();
    } catch (e) { /* ignore */ }
    setSaving(false);
  };
  const remove = async (id) => { await axios.delete(`/bundles/${id}`).then(load).catch(() => {}); };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={newBundle}>Создать связку</Button>
      </Box>

      {bundles.length === 0 ? (
        <Card sx={{ p: 5, textAlign: 'center' }}>
          <Typography sx={{ color: 'text.secondary' }}>Связок пока нет. Создай шаблон кампании.</Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {bundles.map((bn) => (
            <Card key={bn.id} sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{bn.name}</Typography>
                    {bn.objective && <Chip size="small" label={objLabel(bn.objective)} color="primary" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
                    {(bn.config?.countries || []).length > 0 && <Chip size="small" label={bn.config.countries.join(', ')} color="success" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
                    {(bn.config?.age_min || bn.config?.age_max) && <Chip size="small" label={`${bn.config.age_min}-${bn.config.age_max}`} variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
                    {bn.config?.budget_amount ? <Chip size="small" label={`$${bn.config.budget_amount}/${bn.config.budget_type === 'lifetime' ? 'срок' : 'день'}`} variant="outlined" sx={{ height: 20, fontSize: 11 }} /> : null}
                  </Box>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {bn.config?.auto_placements === false ? `${(bn.config?.placements || []).length} плейсментов` : 'Авто-плейсменты'}
                    {bn.config?.link_url ? `, ${bn.config.link_url}` : ''}
                  </Typography>
                </Box>
                <Box>
                  <IconButton size="small" onClick={() => editBundle(bn)}><EditRoundedIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => remove(bn.id)} sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: '"Space Grotesk", sans-serif' }}>{b.id ? 'Изменить связку' : 'Новая связка'}</Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 1 }}>Шаблон кампании для Marketing API — поля структурные, так их принимает Facebook.</Typography>
          <TextField label="Название связки" fullWidth size="small" value={b.name} onChange={(e) => setB((p) => ({ ...p, name: e.target.value }))} sx={{ mt: 2, ...ROUND }} />

          <Sec>Кампания</Sec>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 220, ...ROUND }}>
              <InputLabel>Цель</InputLabel>
              <Select label="Цель" value={b.objective} onChange={(e) => setB((p) => ({ ...p, objective: e.target.value }))} sx={{ borderRadius: '6px' }}>
                {OBJECTIVES.map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 210, ...ROUND }}>
              <InputLabel>Уровень бюджета</InputLabel>
              <Select label="Уровень бюджета" value={b.config.budget_level} onChange={(e) => setCfg('budget_level', e.target.value)} sx={{ borderRadius: '6px' }}>
                <MenuItem value="campaign">Кампания (CBO)</MenuItem>
                <MenuItem value="adset">Группа (ABO)</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150, ...ROUND }}>
              <InputLabel>Бюджет</InputLabel>
              <Select label="Бюджет" value={b.config.budget_type} onChange={(e) => setCfg('budget_type', e.target.value)} sx={{ borderRadius: '6px' }}>
                {BUDGET_TYPES.map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Сумма ($)" type="number" size="small" value={b.config.budget_amount} onChange={(e) => setCfg('budget_amount', e.target.value)} sx={{ width: 130, ...ROUND }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 240, ...ROUND }}>
              <InputLabel>Стратегия ставки</InputLabel>
              <Select label="Стратегия ставки" value={b.config.bid_strategy} onChange={(e) => setCfg('bid_strategy', e.target.value)} sx={{ borderRadius: '6px' }}>
                {BID_STRATEGIES.map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
              </Select>
            </FormControl>
            {b.config.bid_strategy !== 'LOWEST_COST_WITHOUT_CAP' && (
              <TextField label="Ставка ($)" type="number" size="small" value={b.config.bid_amount} onChange={(e) => setCfg('bid_amount', e.target.value)} sx={{ width: 130, ...ROUND }} />
            )}
            <FormControl size="small" sx={{ minWidth: 220, ...ROUND }}>
              <InputLabel>Оптимизация</InputLabel>
              <Select label="Оптимизация" value={b.config.optimization_goal} onChange={(e) => setCfg('optimization_goal', e.target.value)} sx={{ borderRadius: '6px' }}>
                {OPT_GOALS.map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          {b.config.optimization_goal === 'LEAD_GENERATION' && (
            <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '8px' }}>
              Оптимизация «Лид-формы» требует моментальной формы в креативе — обычная ссылка не подойдёт. Для залива по ссылке выбери Трафик (клики/просмотры LP) или Продажи+пиксель.
            </Alert>
          )}
          {(b.objective === 'OUTCOME_SALES' || _CONV_OPT.includes(b.config.optimization_goal)) && (
            <Alert severity="info" sx={{ mt: 1.5, borderRadius: '8px' }}>
              Для конверсий/продаж на каждом кабинете при запуске нужно указать Pixel ID. «Окно атрибуции» применится к группе объявлений.
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 170, ...ROUND }}>
              <InputLabel>Оплата</InputLabel>
              <Select label="Оплата" value={b.config.billing_event} onChange={(e) => setCfg('billing_event', e.target.value)} sx={{ borderRadius: '6px' }}>
                {BILLING.map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 260, ...ROUND }}>
              <InputLabel>Окно атрибуции</InputLabel>
              <Select label="Окно атрибуции" value={b.config.attribution} onChange={(e) => setCfg('attribution', e.target.value)} sx={{ borderRadius: '6px' }}>
                {ATTRIBUTION.map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          {b.objective === 'OUTCOME_SALES' && (
            <FormControl size="small" sx={{ mt: 1.5, minWidth: 240, ...ROUND }}>
              <InputLabel>Событие конверсии</InputLabel>
              <Select label="Событие конверсии" value={b.config.conversion_event} onChange={(e) => setCfg('conversion_event', e.target.value)} sx={{ borderRadius: '6px' }}>
                {CONV_EVENTS.map((o) => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          <Sec>Аудитория</Sec>
          <Autocomplete
            multiple size="small" options={COUNTRIES} disableCloseOnSelect
            getOptionLabel={(o) => `${o.l} (${o.v})`}
            isOptionEqualToValue={(o, v) => o.v === v.v}
            value={COUNTRIES.filter((c) => (b.config.countries || []).includes(c.v))}
            onChange={(e, val) => setCfg('countries', val.map((x) => x.v))}
            renderInput={(params) => <TextField {...params} label="Гео (страны)" placeholder="Начни вводить страну" sx={{ ...ROUND }} />}
          />
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
            <TextField label="Возраст от" type="number" size="small" value={b.config.age_min} onChange={(e) => setCfg('age_min', e.target.value)} sx={{ width: 110, ...ROUND }} />
            <TextField label="до" type="number" size="small" value={b.config.age_max} onChange={(e) => setCfg('age_max', e.target.value)} sx={{ width: 110, ...ROUND }} />
            <FormControl size="small" sx={{ minWidth: 130, ...ROUND }}>
              <InputLabel>Пол</InputLabel>
              <Select label="Пол" value={b.config.gender} onChange={(e) => setCfg('gender', e.target.value)} sx={{ borderRadius: '6px' }}>
                {GENDERS.map((g) => <MenuItem key={g.v} value={g.v}>{g.l}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160, ...ROUND }}>
              <InputLabel>Платформа</InputLabel>
              <Select label="Платформа" value={b.config.device_platform} onChange={(e) => setCfg('device_platform', e.target.value)} sx={{ borderRadius: '6px' }}>
                {DEVICE_PLATFORMS.map((d) => <MenuItem key={d.v} value={d.v}>{d.l}</MenuItem>)}
              </Select>
            </FormControl>
            {b.config.device_platform === 'mobile' && (
              <FormControl size="small" sx={{ minWidth: 130, ...ROUND }}>
                <InputLabel>ОС</InputLabel>
                <Select label="ОС" value={b.config.mobile_os} onChange={(e) => setCfg('mobile_os', e.target.value)} sx={{ borderRadius: '6px' }}>
                  {MOBILE_OS.map((d) => <MenuItem key={d.v} value={d.v}>{d.l}</MenuItem>)}
                </Select>
              </FormControl>
            )}
          </Box>
          <TextField label="Интересы / детальный таргетинг" placeholder="напр. Real estate, Investing" fullWidth size="small" multiline rows={2}
            value={b.config.interests} onChange={(e) => setCfg('interests', e.target.value)} sx={{ mt: 1.5, ...ROUND }}
            helperText="Через запятую. При заливке подберём точные ID через поиск интересов Facebook." />
          <TextField label="Языки (ID локалей FB через запятую, напр. 6, 24)" fullWidth size="small"
            value={b.config.languages} onChange={(e) => setCfg('languages', e.target.value)} sx={{ mt: 1.5, ...ROUND }}
            helperText="Необязательно. 6 = English (US), 24 = English (UK)." />

          <Sec>Плейсменты</Sec>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Switch checked={b.config.auto_placements} onChange={(e) => setCfg('auto_placements', e.target.checked)} />
            <Typography sx={{ fontSize: 13 }}>Автоматические плейсменты (рекомендуется)</Typography>
          </Box>
          {!b.config.auto_placements && (
            <Box sx={{ mt: 1 }}>
              <FormControl size="small" fullWidth sx={{ ...ROUND }}>
                <InputLabel>Плейсменты</InputLabel>
                <Select multiple label="Плейсменты" value={b.config.placements} onChange={(e) => setCfg('placements', e.target.value)}
                  input={<OutlinedInput label="Плейсменты" sx={{ borderRadius: '6px' }} />}
                  renderValue={(sel) => sel.map((p) => labelOf(PLACEMENTS, p)).join(', ')}>
                  {PLACEMENTS.map((p) => <MenuItem key={p.v} value={p.v}>{p.l}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            <Switch checked={b.config.targeting_expansion} onChange={(e) => setCfg('targeting_expansion', e.target.checked)} />
            <Typography sx={{ fontSize: 13 }}>Расширение таргетинга (Advantage+ аудитория)</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Switch checked={b.config.wifi_only} onChange={(e) => setCfg('wifi_only', e.target.checked)} />
            <Typography sx={{ fontSize: 13 }}>Только при подключении к Wi-Fi</Typography>
          </Box>

          <Sec>Названия (макросы)</Sec>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mb: 1 }}>
            Макросы: {'{{bundle.name}}'}, {'{{adset.number}}'}, {'{{ad.number}}'}. В текстах ниже работает spintax: {'{вариант1|вариант2}'}.
          </Typography>
          <TextField label="Название кампании" fullWidth size="small" value={b.config.campaign_name} onChange={(e) => setCfg('campaign_name', e.target.value)} sx={{ ...ROUND }} />
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
            <TextField label="Название адсета" size="small" value={b.config.adset_name} onChange={(e) => setCfg('adset_name', e.target.value)} sx={{ flex: 1, minWidth: 200, ...ROUND }} />
            <TextField label="Название объявления" size="small" value={b.config.ad_name} onChange={(e) => setCfg('ad_name', e.target.value)} sx={{ flex: 1, minWidth: 200, ...ROUND }} />
          </Box>

          <Sec>Объявление</Sec>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mb: 1 }}>Страница FB, Instagram и ссылка задаются при запуске — свои для каждого аккаунта. Ссылку ниже можно оставить как значение по умолчанию.</Typography>
          <TextField label="Ссылка по умолчанию (URL, необязательно)" placeholder="https://" fullWidth size="small" value={b.config.link_url} onChange={(e) => setCfg('link_url', e.target.value)} sx={{ ...ROUND }} />
          <TextField label="Основной текст" fullWidth size="small" multiline rows={2} value={b.config.primary_text} onChange={(e) => setCfg('primary_text', e.target.value)} sx={{ mt: 1.5, ...ROUND }} helperText="Доступен spintax: {вариант1|вариант2}" />
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
            <TextField label="Заголовок" size="small" value={b.config.headline} onChange={(e) => setCfg('headline', e.target.value)} sx={{ flex: 1, minWidth: 200, ...ROUND }} />
            <TextField label="Описание" size="small" value={b.config.description} onChange={(e) => setCfg('description', e.target.value)} sx={{ flex: 1, minWidth: 200, ...ROUND }} />
          </Box>
          <FormControl size="small" sx={{ mt: 1.5, minWidth: 240, ...ROUND }}>
            <InputLabel>Кнопка (CTA)</InputLabel>
            <Select label="Кнопка (CTA)" value={b.config.cta} onChange={(e) => setCfg('cta', e.target.value)} sx={{ borderRadius: '6px' }}>
              {CTAS.map((c) => <MenuItem key={c.v} value={c.v}>{c.l}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: 'text.secondary' }}>Закрыть</Button>
          <Button variant="contained" onClick={save} disabled={saving || !b.name.trim()}>Сохранить</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ---------- Вкладка: Залить связку (мастер) ---------- */
function UploadWizard({ onDone }) {
  const [bundles, setBundles] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [bundleId, setBundleId] = useState('');
  const [targets, setTargets] = useState({});   // accountId -> { adAccountId, pageId, instagramId, pixelId, pages, pagesErr, loading }
  const [creativeIds, setCreativeIds] = useState([]);
  const [activate, setActivate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [linkAll, setLinkAll] = useState('');
  const [budgetAll, setBudgetAll] = useState('');
  const [pauseBetween, setPauseBetween] = useState('');
  const [scheduleStart, setScheduleStart] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [scheduleOn, setScheduleOn] = useState(false);
  const toggleCreative = (id) => setCreativeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  useEffect(() => {
    axios.get('/bundles').then((r) => setBundles(r.data)).catch(() => {});
    axios.get('/accounts').then((r) => setAccounts(r.data)).catch(() => {});
  }, []);

  const bundle = bundles.find((x) => x.id === bundleId);
  const isSales = bundle && bundle.objective === 'OUTCOME_SALES';
  const setTgt = (accId, patch) => setTargets((prev) => ({ ...prev, [accId]: { ...prev[accId], ...patch } }));
  const headSx = { fontSize: 11, fontWeight: 700, color: 'text.secondary', letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' };

  const addAccount = (acc) => {
    const ads = acc.ad_accounts || [];
    const defLink = (bundle && bundle.config && bundle.config.link_url) || '';
    const defBudget = (bundle && bundle.config && bundle.config.budget_amount) || '';
    setTargets((prev) => ({ ...prev, [acc.id]: { adAccountId: ads.length === 1 ? ads[0].id : '', pageId: '', pixelId: '', linkUrl: defLink, budget: defBudget, pages: [], pagesErr: '', loading: true } }));
    axios.get(`/accounts/${acc.id}/pages`)
      .then((r) => setTgt(acc.id, { pages: r.data.pages || [], pagesErr: r.data.error || '', loading: false, pageId: (r.data.pages && r.data.pages.length === 1) ? r.data.pages[0].id : '' }))
      .catch(() => setTgt(acc.id, { pages: [], pagesErr: 'Не удалось получить страницы', loading: false }));
  };
  const removeAccount = (accId) => setTargets((prev) => { const n = { ...prev }; delete n[accId]; return n; });
  const onSelectAccounts = (e, vals) => {
    const ids = vals.map((v) => v.id);
    vals.forEach((v) => { if (!targets[v.id]) addAccount(v); });
    Object.keys(targets).forEach((id) => { if (!ids.includes(id)) removeAccount(id); });
  };
  const applyToAll = (patch) => setTargets((prev) => { const n = { ...prev }; Object.keys(n).forEach((id) => { n[id] = { ...n[id], ...patch }; }); return n; });
  const groups = [...new Set(accounts.map((a) => a.group).filter(Boolean))];
  const addGroup = () => accounts.filter((a) => (a.group || '') === groupFilter && !targets[a.id]).forEach(addAccount);
  const allTags = [...new Set(accounts.flatMap((a) => a.tags || []))].sort((x, y) => x.toLowerCase().localeCompare(y.toLowerCase()));
  const addByTag = () => accounts.filter((a) => (a.tags || []).includes(tagFilter) && !targets[a.id]).forEach(addAccount);

  const launch = async () => {
    setBusy(true); setError('');
    const payloadTargets = Object.entries(targets).map(([accId, t]) => ({
      account_id: accId, ad_account_id: t.adAccountId, page_id: t.pageId,
      pixel_id: t.pixelId || undefined, link_url: t.linkUrl || undefined,
      budget_amount: t.budget ? Number(t.budget) : undefined,
    }));
    try {
      await axios.post('/autoupload/launch', {
        bundle_id: bundleId, creative_ids: creativeIds, activate, targets: payloadTargets,
        pause_between: Number(pauseBetween) || 0,
        schedule_start: scheduleStart ? new Date(scheduleStart).toISOString() : undefined,
      });
      if (onDone) onDone();   // → переключит на вкладку «Очередь», прогресс идёт там
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.detail) || 'Не удалось запустить');
    } finally { setBusy(false); }
  };

  const chosen = Object.keys(targets);
  const selected = accounts.filter((a) => targets[a.id]);
  const ready = bundleId && chosen.length > 0 && creativeIds.length > 0
    && chosen.every((id) => targets[id].adAccountId && targets[id].pageId && targets[id].linkUrl && targets[id].budget) && !busy;

  return (
    <Card sx={{ p: 3 }}>
      <FormControl size="small" fullWidth sx={{ mb: 2, ...ROUND }}>
        <InputLabel>Связка</InputLabel>
        <Select label="Связка" value={bundleId} onChange={(e) => setBundleId(e.target.value)} sx={{ borderRadius: '6px' }}>
          {bundles.length === 0 && <MenuItem value="" disabled>сначала создай связку</MenuItem>}
          {bundles.map((bn) => <MenuItem key={bn.id} value={bn.id}>{bn.name}</MenuItem>)}
        </Select>
      </FormControl>
      {bundle && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
          <Chip size="small" variant="outlined" label={objLabel(bundle.objective)} />
          {(bundle.config?.countries || []).length > 0 && <Chip size="small" variant="outlined" label={bundle.config.countries.join(', ')} />}
          <Chip size="small" variant="outlined" label={`${bundle.config?.age_min}-${bundle.config?.age_max}`} />
          <Chip size="small" variant="outlined" label={bundle.config?.auto_placements === false ? `${(bundle.config?.placements || []).length} плейсментов` : 'Авто-плейсменты'} />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5, alignItems: 'center' }}>
        <TextField size="small" type="number" label="Пауза между кабинетами, сек" value={pauseBetween} onChange={(e) => setPauseBetween(e.target.value)} sx={{ width: 230, ...ROUND }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Switch size="small" checked={scheduleOn} onChange={(e) => { setScheduleOn(e.target.checked); if (!e.target.checked) setScheduleStart(''); }} />
          <Typography sx={{ fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap' }}>Запуск по времени</Typography>
        </Box>
        {scheduleOn && (
          <TextField size="small" type="datetime-local" InputLabelProps={{ shrink: true }} value={scheduleStart}
            onChange={(e) => setScheduleStart(e.target.value)} sx={{ width: 210, ...ROUND }} />
        )}
        {groups.length > 0 && (
          <>
            <FormControl size="small" sx={{ minWidth: 150, ...ROUND }}>
              <InputLabel>Группа</InputLabel>
              <Select label="Группа" value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} sx={{ borderRadius: '6px' }}>
                <MenuItem value="">—</MenuItem>
                {groups.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="outlined" size="small" disabled={!groupFilter} onClick={addGroup}>Добавить группу</Button>
          </>
        )}
        {allTags.length > 0 && (
          <>
            <FormControl size="small" sx={{ minWidth: 150, ...ROUND }}>
              <InputLabel>Тег</InputLabel>
              <Select label="Тег" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} sx={{ borderRadius: '6px' }}>
                <MenuItem value="">—</MenuItem>
                {allTags.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="outlined" size="small" disabled={!tagFilter} onClick={addByTag}>Добавить по тегу</Button>
          </>
        )}
      </Box>

      <Autocomplete
        multiple size="small" options={accounts} disableCloseOnSelect
        getOptionLabel={(o) => o.name || o.id}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        value={selected}
        onChange={onSelectAccounts}
        renderInput={(params) => <TextField {...params} label="Выберите аккаунты" placeholder="Поиск аккаунта" sx={{ ...ROUND }} />}
      />

      {chosen.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5, alignItems: 'center' }}>
            <TextField size="small" label="Ссылка для всех" placeholder="https://" value={linkAll} onChange={(e) => setLinkAll(e.target.value)} sx={{ flex: 1, minWidth: 220, ...ROUND }} />
            <Button variant="outlined" size="small" disabled={!linkAll} onClick={() => applyToAll({ linkUrl: linkAll })}>Применить</Button>
            <TextField size="small" type="number" label="Бюджет для всех ($)" value={budgetAll} onChange={(e) => setBudgetAll(e.target.value)} sx={{ width: 170, ...ROUND }} />
            <Button variant="outlined" size="small" disabled={!budgetAll} onClick={() => applyToAll({ budget: budgetAll })}>Применить</Button>
          </Box>
          <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: '10px' }}>
            <Table size="small" sx={{ minWidth: isSales ? 920 : 780 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>Аккаунт</TableCell>
                  <TableCell sx={headSx}>Кабинет</TableCell>
                  <TableCell sx={headSx}>Фан-пейдж</TableCell>
                  {isSales && <TableCell sx={headSx}>Pixel ID</TableCell>}
                  <TableCell sx={headSx}>Ссылка</TableCell>
                  <TableCell sx={headSx}>Бюджет $</TableCell>
                  <TableCell sx={headSx} />
                </TableRow>
              </TableHead>
              <TableBody>
                {selected.map((a) => {
                  const t = targets[a.id];
                  const ads = a.ad_accounts || [];
                  return (
                    <TableRow key={a.id}>
                      <TableCell sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>{a.name || a.id}</TableCell>
                      <TableCell>
                        <Select size="small" value={t.adAccountId} displayEmpty onChange={(e) => setTgt(a.id, { adAccountId: e.target.value })} sx={{ minWidth: 140, borderRadius: '6px' }}>
                          {ads.length === 0 && <MenuItem value="" disabled>нет кабинетов</MenuItem>}
                          {ads.map((ad) => <MenuItem key={ad.id} value={ad.id}>{ad.name || ad.id}</MenuItem>)}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select size="small" value={t.pageId} displayEmpty onChange={(e) => setTgt(a.id, { pageId: e.target.value })} sx={{ minWidth: 150, borderRadius: '6px' }}>
                          {t.loading && <MenuItem value="" disabled>загрузка…</MenuItem>}
                          {!t.loading && t.pages.length === 0 && <MenuItem value="" disabled>{t.pagesErr || 'нет страниц'}</MenuItem>}
                          {t.pages.map((p) => <MenuItem key={p.id} value={p.id}>{p.name || p.id}</MenuItem>)}
                        </Select>
                      </TableCell>
                      {isSales && <TableCell><TextField size="small" value={t.pixelId} onChange={(e) => setTgt(a.id, { pixelId: e.target.value })} sx={{ width: 130, ...ROUND }} /></TableCell>}
                      <TableCell><TextField size="small" placeholder="https://" value={t.linkUrl} onChange={(e) => setTgt(a.id, { linkUrl: e.target.value })} sx={{ minWidth: 180, ...ROUND }} /></TableCell>
                      <TableCell><TextField size="small" type="number" value={t.budget} onChange={(e) => setTgt(a.id, { budget: e.target.value })} sx={{ width: 90, ...ROUND }} /></TableCell>
                      <TableCell><IconButton size="small" onClick={() => removeAccount(a.id)} sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}

      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 2.5, mb: 1 }}>Креативы (одно объявление на каждый выбранный креатив)</Typography>
      <CreativeLibrary selectable selected={creativeIds} onToggle={toggleCreative} />

      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
        <Switch checked={activate} onChange={(e) => setActivate(e.target.checked)} />
        <Typography sx={{ fontSize: 13 }}>Запустить сразу (иначе на паузе — рекомендуется)</Typography>
      </Box>
      {activate && <Alert severity="warning" sx={{ mt: 1, borderRadius: '8px' }}>Кампании начнут тратить бюджет сразу. Безопаснее оставить на паузе и проверить в Ads Manager.</Alert>}
      {error && <Alert severity="error" sx={{ mt: 2, borderRadius: '8px' }}>{error}</Alert>}

      <Divider sx={{ my: 2.5 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<CloudUploadRoundedIcon />} disabled={!ready} onClick={launch}>
          {busy ? 'Ставлю в очередь…' : (chosen.length > 1 ? `Залить на ${chosen.length} акк.` : 'Создать кампанию')}
        </Button>
      </Box>
    </Card>
  );
}

/* ---------- Вкладка: Очередь (живой прогресс) ---------- */
function JobCard({ job, onRemove }) {
  const [j, setJ] = useState(job);
  const [open, setOpen] = useState(job.status === 'running' || job.status === 'queued');
  const active = j.status === 'running' || j.status === 'queued';

  // пока задание активно — опрашиваем его каждые 2 сек
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => {
      axios.get(`/upload-jobs/${j.id}`).then((r) => setJ(r.data)).catch(() => {});
    }, 2000);
    return () => clearInterval(t);
  }, [active, j.id]);

  const total = j.total || (j.progress || []).length || 0;
  const finished = (j.done || 0) + (j.failed || 0);
  const pct = total ? Math.round((finished / total) * 100) : (active ? 0 : 100);

  return (
    <Card sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flex: 1, cursor: 'pointer' }} onClick={() => setOpen((v) => !v)}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{j.bundle_name || 'Связка'}</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {finished}/{total} кабинетов{j.failed ? ` · ошибок ${j.failed}` : ''} · {j.created_at ? new Date(j.created_at).toLocaleString('ru-RU') : ''}
          </Typography>
        </Box>
        <Chip size="small" label={STATUS_LABEL[j.status] || j.status} color={STATUS_COLOR[j.status] || 'default'} variant="outlined" sx={{ fontWeight: 600 }} />
        <IconButton size="small" disabled={active} onClick={() => onRemove(j.id)} sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
      </Box>

      <LinearProgress
        variant={active && !finished ? 'indeterminate' : 'determinate'} value={pct}
        color={j.status === 'error' ? 'error' : j.status === 'partial' ? 'warning' : 'success'}
        sx={{ mt: 1.5, height: 6, borderRadius: 3 }}
      />

      <Collapse in={open}>
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {(j.progress || []).map((p) => (
            <Box key={p.account_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
              <Typography sx={{ fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.name || p.account_id}
                {p.status === 'done' && p.campaign_id ? <span style={{ color: 'var(--mui-palette-text-secondary)', fontSize: 11 }}> · объявлений {p.ads || 0}</span> : null}
                {p.status === 'error' && p.error ? <span style={{ color: '#d33', fontSize: 11 }}> · {p.error}</span> : null}
              </Typography>
              <Chip size="small" label={ITEM_LABEL[p.status] || p.status} color={ITEM_COLOR[p.status] || 'default'} variant="outlined" sx={{ fontWeight: 600, minWidth: 72 }} />
            </Box>
          ))}
        </Box>
      </Collapse>
    </Card>
  );
}

function QueueTab({ refreshKey }) {
  const [jobs, setJobs] = useState([]);
  const load = useCallback(() => { axios.get('/upload-jobs').then((r) => setJobs(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  // подхватываем новые задания, пока есть активные (или сразу после запуска)
  useEffect(() => {
    const anyActive = jobs.some((j) => j.status === 'running' || j.status === 'queued');
    if (!anyActive) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [jobs, load]);
  const remove = async (id) => { await axios.delete(`/upload-jobs/${id}`).then(load).catch(() => {}); };

  if (jobs.length === 0) return <Card sx={{ p: 5, textAlign: 'center' }}><Typography sx={{ color: 'text.secondary' }}>Очередь пуста.</Typography></Card>;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {jobs.map((j) => <JobCard key={j.id} job={j} onRemove={remove} />)}
    </Box>
  );
}

/* ---------- Библиотека креативов ---------- */
function fmtSize(b) {
  if (!b) return '';
  return b > 1024 * 1024 ? (b / 1024 / 1024).toFixed(1) + ' МБ' : Math.round(b / 1024) + ' КБ';
}

// SHA-256 файла в hex (для дедупа на сервере)
async function sha256hex(file) {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function CreativeLibrary({ selectable = false, selected = [], onToggle }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);   // {done, total}
  const inputRef = useRef(null);

  const load = useCallback(() => { axios.get('/creatives').then((r) => setItems(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    setProgress({ done: 0, total: files.length });
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > 200 * 1024 * 1024) {
        window.alert('Файл слишком большой: ' + file.name + ' (макс 200 МБ)');
        setProgress({ done: i + 1, total: files.length });
        continue;
      }
      try {
        const sha256 = await sha256hex(file);
        const meta = { filename: file.name, mime: file.type, size: file.size, sha256 };
        // 1) ссылка на прямую загрузку (или сервер говорит, что файл уже есть — дедуп)
        const r = await axios.post('/creatives/upload-url', meta);
        const { exists, upload_url, path } = r.data;
        // 2) если нет — грузим файл НАПРЯМУЮ в Storage (минуя бэкенд), обычным fetch без app-токена
        if (!exists) {
          const put = await fetch(upload_url, {
            method: 'PUT', body: file,
            headers: { 'Content-Type': file.type || 'application/octet-stream', 'x-upsert': 'true' },
          });
          if (!put.ok) throw new Error('Storage ' + put.status);
        }
        // 3) регистрируем в библиотеке
        await axios.post('/creatives/finalize', { ...meta, path });
      } catch (e) {
        window.alert('Не загрузилось: ' + file.name + ' — ' + ((e.response && e.response.data && e.response.data.detail) || e.message || 'ошибка'));
      }
      setProgress({ done: i + 1, total: files.length });
    }
    setBusy(false);
    setProgress(null);
    load();
  };

  const remove = async (id) => { await axios.delete(`/creatives/${id}`).then(load).catch(() => {}); };

  return (
    <Box>
      <Box
        onClick={() => inputRef.current && inputRef.current.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
        sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: '16px', p: 3, textAlign: 'center', cursor: 'pointer', mb: 2, bgcolor: 'action.hover', transition: 'border-color .15s', '&:hover': { borderColor: 'primary.main' } }}
      >
        <CloudUploadRoundedIcon sx={{ fontSize: 30, color: 'primary.main', mb: 0.5 }} />
        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
          {busy ? (progress ? `Загрузка ${progress.done}/${progress.total}…` : 'Загрузка…') : 'Перетащи файлы сюда или нажми для выбора'}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>Картинки и видео (до 200 МБ). Хранятся 45 дней, дубликаты не занимают место.</Typography>
        <input ref={inputRef} type="file" multiple hidden accept="image/*,video/*" onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }} />
      </Box>

      {items.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center', py: 2 }}>Пока пусто.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {items.map((c) => {
            const isSel = selected.includes(c.id);
            const url = c.url;
            const isImg = (c.mime || '').startsWith('image') && !!url;
            return (
              <Box key={c.id} onClick={selectable ? () => onToggle && onToggle(c.id) : undefined}
                sx={{ width: 132, borderRadius: '6px', overflow: 'hidden', position: 'relative', border: '2px solid', borderColor: isSel ? 'primary.main' : 'divider', cursor: selectable ? 'pointer' : 'default', bgcolor: 'background.paper' }}>
                <Box sx={{ height: 92, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {isImg
                    ? <img src={url} alt={c.filename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <Typography sx={{ fontSize: 11, color: 'text.secondary', px: 1, textAlign: 'center' }}>🎬 видео</Typography>}
                </Box>
                <Box sx={{ p: 0.75 }}>
                  <Typography noWrap sx={{ fontSize: 11, fontWeight: 600 }}>{c.filename || 'файл'}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{fmtSize(c.size)}</Typography>
                    {!selectable && (
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); remove(c.id); }} sx={{ p: 0.25, '&:hover': { color: 'error.main' } }}>
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                {selectable && isSel && (
                  <Box sx={{ position: 'absolute', top: 4, right: 4, width: 18, height: 18, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>✓</Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default function AutoUploadPage() {
  const [tab, setTab] = useState(0);
  const [queueKey, setQueueKey] = useState(0);

  return (
    <Page title="Автозалив" subtitle="Связки (шаблоны кампаний), заливка по кабинетам и очередь">
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
        <Tab label="Связки" />
        <Tab label="Залить связку" />
        <Tab label="Очередь" />
        <Tab label="Креативы" />
      </Tabs>
      {tab === 0 && <BundlesTab />}
      {tab === 1 && <UploadWizard onDone={() => { setQueueKey((k) => k + 1); setTab(2); }} />}
      {tab === 2 && <QueueTab refreshKey={queueKey} />}
      {tab === 3 && <CreativeLibrary />}
    </Page>
  );
}
